import { useCallback, useEffect, useMemo, useState, type MutableRefObject } from 'react'
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import { Select } from '../Common'
import { Button } from '../Common/Button'
import { firestoreDb } from '../../services/firebase'
import { logAuditEvent, snapshotAuditTrail } from '../../services/auditLedgerService'
import { useAuth } from '../../context/useAuth'
import { useDisplayName } from '../../hooks/useDisplayName'
import { useNls } from '../../nls/nlsProvider'
import type { Workspace } from '../../context/WorkspaceContext'
import {
  WORKSPACE_PERMISSION_GROUPS,
  normalizeWorkspaceActionPermissions,
  type WorkspaceActionPermissions,
} from '../../utils/workspacePermissions'
import { getWorkspacePermissionRoleOptions, workspacePermissionSaveLabel } from './workspacePermissionManagerModel'

export type WorkspacePermissionManagerSaveHandle = {
  save: () => Promise<void>
  reset?: () => void
}

type WorkspacePermissionManagerProps = {
  workspace: Workspace
  /** Optional external observer for chrome that owns the save affordance. */
  onDirtyChange?: (isDirty: boolean) => void
  /** Optional imperative bridge for an external save affordance. */
  saveHandle?: MutableRefObject<WorkspacePermissionManagerSaveHandle | null>
  /** Keeps the existing inline save button unless an external affordance owns it. */
  hideInlineSaveButton?: boolean
  /** When true, hides the legacy header chrome so SettingsSection owns title and description. */
  hideHeader?: boolean
}

export const WorkspacePermissionManager = ({
  workspace,
  onDirtyChange,
  saveHandle: saveHandleRef,
  hideInlineSaveButton = false,
  hideHeader = false,
}: WorkspacePermissionManagerProps) => {
  const { t } = useNls()
  const { user, userProfile } = useAuth()
  const currentDisplayName = useDisplayName(userProfile ?? user)
  const [draft, setDraft] = useState<WorkspaceActionPermissions>(() => normalizeWorkspaceActionPermissions(workspace.actionPermissions))
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const normalizedDraft = useMemo(() => normalizeWorkspaceActionPermissions(draft), [draft])
  const isDirty = useMemo(
    () => JSON.stringify(normalizedDraft) !== JSON.stringify(normalizeWorkspaceActionPermissions(workspace.actionPermissions)),
    [normalizedDraft, workspace.actionPermissions],
  )

  const [prevSync, setPrevSync] = useState({
    id: workspace.id,
    permissions: workspace.actionPermissions,
  })
  if (
    prevSync.id !== workspace.id ||
    prevSync.permissions !== workspace.actionPermissions
  ) {
    setPrevSync({
      id: workspace.id,
      permissions: workspace.actionPermissions,
    })
    setDraft(normalizeWorkspaceActionPermissions(workspace.actionPermissions))
  }

  const save = useCallback(async () => {
    if (isSaving) return
    setIsSaving(true)
    setMessage(null)
    try {
      if (!user) throw new Error(t('NLSBoardSettingsPermManagerSignInRequired'))
      const nextPermissions = normalizeWorkspaceActionPermissions(normalizedDraft)
      const workspaceRef = doc(firestoreDb, 'workspaces', workspace.id)
      await runTransaction(firestoreDb, async (transaction) => {
        const current = await transaction.get(workspaceRef)
        if (!current.exists()) throw new Error(t('NLSBoardSettingsPermManagerWorkspaceNotFound'))
        transaction.update(workspaceRef, { actionPermissions: nextPermissions, actionPermissionsUpdatedAt: serverTimestamp() })
        logAuditEvent(firestoreDb, transaction, current.data(), { workspaceId: workspace.id, actorId: user.uid, actorName: currentDisplayName || 'Workspace Admin', action: 'WORKSPACE_PERMISSIONS_UPDATED', actionType: 'UPDATE', activityType: 'ROLE_CHANGE', targetId: workspace.id, nodeType: 'WORKSPACE_PERMISSIONS', targetTitle: workspace.name, summary: 'Updated workspace action permissions.', snapshot: snapshotAuditTrail(current.data().actionPermissions ?? {}, nextPermissions, Object.keys(nextPermissions)) })
      })
      setMessage(t('NLSBoardSettingsPermManagerUpdated'))
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('NLSBoardSettingsPermManagerSaveFailed'))
    } finally {
      setIsSaving(false)
    }
  }, [currentDisplayName, isSaving, normalizedDraft, t, user, workspace.id, workspace.name])

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange])

  useEffect(() => {
    if (!saveHandleRef) return undefined
    saveHandleRef.current = {
      save,
      reset: () => setDraft(normalizeWorkspaceActionPermissions(workspace.actionPermissions)),
    }
    return () => {
      saveHandleRef.current = null
    }
  }, [save, saveHandleRef, workspace.actionPermissions])

  return (
    <section className="workspacePermissionManager">
      {!hideHeader ? (
        <header className="workspacePermissionManagerHeader">
          <div className="workspacePermissionManagerMark"><ShieldOutlinedIcon /></div>
          <div>
            <p className="dropdownLabel">{t('NLSBoardSettingsPermManagerDropdownLabel')}</p>
            <h2>{t('NLSBoardSettingsPermManagerHeading')}</h2>
            <p>{t('NLSBoardSettingsPermManagerLead')}</p>
          </div>
          {!hideInlineSaveButton ? (
            <Button variant="primary" size="md" disabled={isSaving} onClick={() => void save()}>
              {workspacePermissionSaveLabel(isSaving)}
            </Button>
          ) : null}
        </header>
      ) : null}
      {message && <div className="adminBanner">{message}</div>}
      <div className="workspacePermissionGroups">
        {WORKSPACE_PERMISSION_GROUPS.map((group) => (
          <section key={group.key} className="workspacePermissionGroup">
            <header><h3>{group.label}</h3><p>{group.description}</p></header>
            <div className="workspacePermissionRows">
              {group.actions.map(({ action, label, detail }) => (
                <div key={action} className="workspacePermissionRow">
                  <div><strong>{label}</strong><span>{detail}</span></div>
                  <Select
                    trigger="pill"
                    ariaLabel={t('NLSBoardSettingsPermManagerRoleAria', { label })}
                    value={draft[action]}
                    options={getWorkspacePermissionRoleOptions(action)}
                    onChange={(role) => setDraft((current) => ({ ...current, [action]: role }))}
                  />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </section>
  )
}
